import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useAssignees } from "../hooks/useAssignees";

export interface FilterField {
  label: string;
  field: string;
  options: { value: string; label: string }[];
  roleRequired?: string[];
}

export interface AssigneeOption {
  id: string;
  displayName: string;
}

interface FiltersProps {
  filters: Record<string, string>;
  onFilterChange: (field: string, value: string) => void;
  onClearFilters: () => void;
  userRole: string | null;
  availableFilters: FilterField[];
  assigneeOptions?: AssigneeOption[];
  showAssigneeFilter?: boolean;
  branchUserIds?: string[];
  onUsersLoaded?: (data: {
    usersMap: Map<string, string>;
    uidToDisplayName: Map<string, string>;
    assigneeOptions: AssigneeOption[];
  }) => void;
}

const Filters: React.FC<FiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  userRole,
  availableFilters,
  assigneeOptions: propAssigneeOptions,
  showAssigneeFilter = false,
  branchUserIds = [],
  onUsersLoaded,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  // Hook to handle all fetching and mapping logic
  const {
    assigneeOptions: hookAssigneeOptions,
    usersMap,
    uidToDisplayName,
  } = useAssignees({
    userRole,
    branchUserIds,
    enabled: showAssigneeFilter,
  });

  // Keep the callback functional for components that need the maps (like Leads)
  useEffect(() => {
    if (onUsersLoaded && hookAssigneeOptions.length > 0) {
      onUsersLoaded({
        usersMap,
        uidToDisplayName,
        assigneeOptions: hookAssigneeOptions,
      });
    }
  }, [hookAssigneeOptions, usersMap, uidToDisplayName, onUsersLoaded]);

  const toggleFilters = () => setShowFilters((prev) => !prev);

  // Filter visible fields based on roles if specified
  const visibleFilters = availableFilters.filter((f) => {
    if (!f.roleRequired) return true;
    return f.roleRequired.includes(userRole || "");
  });

  // Roles allowed to see the assignee filter
  const canSeeAssignee =
    showAssigneeFilter &&
    ["super admin", "admin", "IT", "Branch Manager"].includes(userRole || "");

  return (
    <div className="w-full">
      <button
        onClick={toggleFilters}
        className="flex items-center gap-1 cursor-pointer"
      >
        <h3>Filters</h3>
        <ChevronDown
          className={`transition-transform duration-200 ${
            showFilters ? "rotate-180" : ""
          }`}
        />
      </button>

      {showFilters && (
        <div className="mt-2">
          <form
            className="flex items-center justify-between border border-gray-400 p-2 rounded text-sm bg-white shadow-sm gap-4"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="flex gap-4">
              {visibleFilters.map((f) => (
                <div key={f.field} className="flex flex-col">
                  <label className="mb-1">{f.label}</label>
                  <select
                    className="border border-gray-300 rounded p-1 cursor-pointer"
                    value={filters[f.field] || ""}
                    onChange={(e) => onFilterChange(f.field, e.target.value)}
                  >
                    <option value="">All</option>
                    {f.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}

              {canSeeAssignee && (
                <div className="flex flex-col">
                  <label className="mb-1">Assignee</label>
                  <select
                    className="border border-gray-300 rounded p-1 cursor-pointer"
                    value={filters.assigneeId || ""}
                    onChange={(e) =>
                      onFilterChange("assigneeId", e.target.value)
                    }
                  >
                    <option value="">All</option>
                    {/* Use prop options if provided, otherwise fallback to hook-managed options */}
                    {(propAssigneeOptions || hookAssigneeOptions).map(
                      (user) => (
                        <option key={user.id} value={user.id}>
                          {user.displayName}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={onClearFilters}
              className="text-sm text-blue-600 hover:underline cursor-pointer"
            >
              Clear Filters
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Filters;
